import { useState, useMemo, useEffect, useRef, useCallback } from "react";
import { useLingui } from "@lingui/react/macro";
import { t, Trans } from "@lingui/macro";
import { timeToMinutes } from "@/lib/time";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { StatusBadge, type StatusTone } from "@/components/ui/status-badge";
import type { MessageDescriptor } from "@lingui/core";
import { msg } from "@lingui/core/macro";
import { errorToastText } from "@/lib/translateBackendError";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import {
  Save,
  Undo,
  Sparkles,
  Grid3x3,
  ChevronsLeftRight,
  Loader2,
  AlertTriangle,
  CheckCircle,
  XCircle,
  FileWarning,
  Archive,
  FilePen,
  History,
  type LucideIcon,
} from "lucide-react";
import { toast } from "sonner";
import type { TariffRate } from "@/types/billing";
import { formatRappen } from "@/types/billing";
import { chfToRappen, rappenToCHF } from "@/lib/money";
import { TariffSmartPatternView } from "./TariffSmartPatternView";
import type { PatternType } from "./usePricingPatternDetection";
import { useBillingSettings } from "@/hooks/useBilling";
import { DEFAULT_CURRENCY } from "@/lib/money";
import { Progress } from "@/components/ui/progress";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { ScrollArea } from "@/components/ui/scroll-area";

interface TariffUnifiedGridProps {
  /** Optional because the generated TariffVersion marks `name` optional; rendered as-is. */
  versionName?: string;
  versionDescription?: string;
  groupType: string;
  status?: string;
  isDefault?: boolean;
  rates: TariffRate[];
  onSave?: (
    rates: TariffRate[],
    options?: {
      onProgress?: (current: number, total: number, service?: string) => void;
      versionTag?: string;
    }
  ) => Promise<void>;
  onApplyPattern?: (pattern: PatternType, rates: TariffRate[]) => void;
  isLoading?: boolean;
  readOnly?: boolean;
  pendingAdjustment?: {
    type: "percentage" | "fixed";
    operation: "increase" | "decrease";
    value: number;
    services?: string[];
    days?: number[];
  } | null;
  onAdjustmentApplied?: () => void;
  pendingCopy?: { sourceService: string; targetService: string } | null;
  onCopyApplied?: () => void;
  versionUpdatedAt?: string; // For optimistic locking
  enableChunking?: boolean; // Option to disable chunking
}

const DAYS = [
  { code: 1, name: "monday" },
  { code: 2, name: "tuesday" },
  { code: 3, name: "wednesday" },
  { code: 4, name: "thursday" },
  { code: 5, name: "friday" },
  { code: 6, name: "saturday" },
  { code: 7, name: "sunday" },
];

type AgeType = "BABY" | "CHILD";
type ViewMode = "smart" | "grid";
type SubsidyType = "STANDARD" | "SUBSIDISED";
type ExtraDayType = "BOOKED" | "EXTRA";
type AgeTypeSelection = "BABY" | "CHILD" | "BOTH";
type SubsidyTypeSelection = "STANDARD" | "SUBSIDISED" | "BOTH";
type ExtraDayTypeSelection = "BOOKED" | "EXTRA" | "BOTH";

const TARIFF_STATUS_STYLE: Record<string, { tone: StatusTone; icon: LucideIcon; label: MessageDescriptor }> = {
  DRAFT: { tone: "neutral", icon: FilePen, label: msg({ id: "common.draft", message: "Draft" }) },
  ACTIVE: { tone: "success", icon: CheckCircle, label: msg({ id: "common.active", message: "Active" }) },
  EXPIRED: { tone: "warning", icon: History, label: msg({ id: "common.expired", message: "Expired" }) },
  ARCHIVED: { tone: "neutral", icon: Archive, label: msg({ id: "common.archived", message: "Archived" }) },
};

function TariffStatusBadge({ status }: { status: string }) {
  const { i18n } = useLingui();
  const style = TARIFF_STATUS_STYLE[status.toUpperCase()];
  if (!style) return <StatusBadge tone="neutral" label={status} />;
  return <StatusBadge tone={style.tone} icon={style.icon} label={i18n._(style.label)} />;
}

export function TariffUnifiedGrid({
  versionName,
  versionDescription,
  groupType,
  status,
  isDefault = false,
  rates,
  onSave,
  onApplyPattern,
  isLoading = false,
  readOnly = false,
  pendingAdjustment,
  onAdjustmentApplied,
  pendingCopy,
  onCopyApplied,
  versionUpdatedAt,
}: TariffUnifiedGridProps) {
  const { i18n } = useLingui();
  const [selectedAgeType, setSelectedAgeType] =
    useState<AgeTypeSelection>("BABY");
  const [selectedSubsidyType, setSelectedSubsidyType] =
    useState<SubsidyTypeSelection>("STANDARD");
  const [selectedExtraDayType, setSelectedExtraDayType] =
    useState<ExtraDayTypeSelection>("BOOKED");
  const [viewMode, setViewMode] = useState<ViewMode>("grid");
  const [selectedPattern, setSelectedPattern] =
    useState<PatternType>("UNIFORM");
  // Track edited rates with their active filter state at time of edit
  const [editedRates, setEditedRates] = useState<
    Map<string, { value: number; filters: string }>
  >(new Map());
  const [originalRates, setOriginalRates] = useState<
    Map<string, number | "mix">
  >(new Map());

  const [hasChanges, setHasChanges] = useState(false);
  const prevRatesRef = useRef(rates);
  const [smartPatternApplyFn, setSmartPatternApplyFn] = useState<
    (() => TariffRate[]) | null
  >(null);

  // Progress tracking state
  const [isSaving, setIsSaving] = useState(false);
  const [saveProgress, setSaveProgress] = useState({
    current: 0,
    total: 0,
    service: "",
  });

  // Verification state
  const [showPreSaveDialog, setShowPreSaveDialog] = useState(false);
  const [preSaveVerification, setPreSaveVerification] = useState<{
    totalRecords: number;
    services: string[];
    combinations: string[];
    sampleChanges: Array<{
      service: string;
      day: string;
      oldRate: number;
      newRate: number;
    }>;
  } | null>(null);
  const [verificationReport, setVerificationReport] = useState<{
    attempted: number;
    succeeded: number;
    failed: Array<{ service: string; day: number; error: string }>;
    discrepancies: Array<{
      service: string;
      day: number;
      expected: number;
      actual: number;
    }>;
  } | null>(null);
  const [showVerificationReport, setShowVerificationReport] = useState(false);

  // Create a stable callback for registering the smart pattern apply function
  // This prevents infinite loops by not changing on every render
  const registerSmartPatternApply = useCallback(
    (applyFn: () => TariffRate[]) => {
      setSmartPatternApplyFn(() => applyFn);
    },
    []
  );

  // Fetch billing settings to get service times
  const { data: billingSettings } = useBillingSettings();
  const currency = billingSettings?.currency ?? DEFAULT_CURRENCY;
  const services = useMemo(
    () => billingSettings?.services || [],
    [billingSettings?.services]
  );

  // Get active services sorted by time (time_from, then time_to)
  const activeServices = useMemo(
    () =>
      services
        .filter((s) => s.active !== false)
        .sort((a, b) => {
          // Sort by time_from first, then by time_to
          const aFromMinutes = timeToMinutes(a.time_from || '00:00');
          const bFromMinutes = timeToMinutes(b.time_from || '00:00');

          if (aFromMinutes !== bFromMinutes) {
            return aFromMinutes - bFromMinutes;
          }

          // If time_from is the same, sort by time_to (descending - longer services first)
          const aToMinutes = timeToMinutes(a.time_to || '23:59');
          const bToMinutes = timeToMinutes(b.time_to || '23:59');
          return bToMinutes - aToMinutes;
        }),
    [services]
  );
  const SERVICE_CODES = activeServices.map((s) => s.code);

  // Helper to build display (used for rendering)
  const getServiceTimes = (serviceCode: string): string => {
    const service = services.find((s) => s.code === serviceCode);
    if (service?.time_from && service?.time_to) {
      return `${service.time_from}-${service.time_to}`;
    }
    return "";
  };

  const getServiceDisplay = (code: string) => {
    const service = services.find((s) => s.code === code);
    let name = code; // Default fallback to code

    if (service) {
      // Get localized name based on current locale
      const locale = i18n.locale;
      const localizedNameKey = `name_${locale}` as keyof typeof service;
      const localizedName = service[localizedNameKey] as string | undefined;

      // Priority: localized name > default name > code
      name = localizedName || service.name || code;
    }

    const times = getServiceTimes(code);
    return { name, times };
  };

  // Percentage layout: Service gets 25%, the rest shares 75%
  const SERVICE_COL_PCT = 25;
  const DAY_COL_PCT = (100 - SERVICE_COL_PCT) / DAYS.length; // 75% / 7

  // Compute the actual arrays from the selection
  const selectedAgeTypes = useMemo(() => {
    if (selectedAgeType === "BOTH") return ["BABY", "CHILD"] as AgeType[];
    return [selectedAgeType] as AgeType[];
  }, [selectedAgeType]);

  const selectedSubsidyTypes = useMemo(() => {
    if (selectedSubsidyType === "BOTH")
      return ["STANDARD", "SUBSIDISED"] as SubsidyType[];
    return [selectedSubsidyType] as SubsidyType[];
  }, [selectedSubsidyType]);

  const selectedExtraDayTypes = useMemo(() => {
    if (selectedExtraDayType === "BOTH")
      return ["BOOKED", "EXTRA"] as ExtraDayType[];
    return [selectedExtraDayType] as ExtraDayType[];
  }, [selectedExtraDayType]);

  // Clear edited rates when server data (rates prop) changes
  useEffect(() => {
    // Check if rates have actually changed (not just first render)
    if (prevRatesRef.current !== rates && rates.length > 0) {
      // Clear all edited rates and reset original rates when new data arrives
      setEditedRates(new Map());
      setOriginalRates(new Map());
      setHasChanges(false);
    }
    prevRatesRef.current = rates;
  }, [rates]);

  // Create a map of current rates for quick lookup
  const rateMap = useMemo(() => {
    const map = new Map<string, number | "mix">();

    SERVICE_CODES.forEach((serviceCode) => {
      DAYS.forEach((day) => {
        const key = `${serviceCode}-${day.code}`;

        // First, collect all actual rates from the data
        const bookedRates: Map<string, number> = new Map();
        const extraRates: Map<string, number> = new Map();

        rates.forEach((rate) => {
          if (
            rate.service_code !== serviceCode ||
            rate.day_of_week !== day.code
          )
            return;

          // Check if this rate matches our age and subsidy filters
          const ageMatches = selectedAgeTypes.includes(
            rate.age_type as AgeType
          );
          const subsidyType = rate.subsidised ? "SUBSIDISED" : "STANDARD";
          const subsidyMatches = selectedSubsidyTypes.includes(subsidyType);

          if (ageMatches && subsidyMatches) {
            const baseKey = `${rate.age_type}-${subsidyType}`;
            if (rate.is_extra_day) {
              extraRates.set(baseKey, rate.daily_rate || 0);
            } else {
              bookedRates.set(baseKey, rate.daily_rate || 0);
            }
          }
        });

        // Now build the matching rates based on what's selected
        const matchingRates: Map<string, number> = new Map();

        selectedAgeTypes.forEach((ageType) => {
          selectedSubsidyTypes.forEach((subsidyType) => {
            const baseKey = `${ageType}-${subsidyType}`;
            const bookedRate = bookedRates.get(baseKey);
            const extraRate = extraRates.get(baseKey);

            selectedExtraDayTypes.forEach((extraDayType) => {
              const comboKey = `${ageType}-${subsidyType}-${extraDayType}`;

              if (extraDayType === "BOOKED") {
                // Use booked rate if available, otherwise use extra rate as fallback
                if (bookedRate !== undefined) {
                  matchingRates.set(comboKey, bookedRate);
                } else if (extraRate !== undefined) {
                  matchingRates.set(comboKey, extraRate);
                }
              } else if (extraDayType === "EXTRA") {
                // Use extra rate if available, otherwise use booked rate as fallback
                if (extraRate !== undefined) {
                  matchingRates.set(comboKey, extraRate);
                } else if (bookedRate !== undefined) {
                  matchingRates.set(comboKey, bookedRate);
                }
              }
            });
          });
        });

        // Check if all values are the same (treating 0 as a distinct value)
        const values = Array.from(matchingRates.values());
        if (values.length > 0) {
          // Use strict equality to properly compare values including 0
          const firstValue = values[0];
          const allSame = values.every((v) => v === firstValue);
          map.set(key, allSame ? firstValue : "mix");
        } else {
          // Only set to 0 if we truly have no rates at all
          map.set(key, 0);
        }
      });
    });

    return map;
  }, [
    rates,
    selectedAgeTypes,
    selectedSubsidyTypes,
    selectedExtraDayTypes,
    SERVICE_CODES,
  ]);

  // Build filter state key for tracking which combinations were edited
  const getCurrentFilterKey = (): string => {
    return `${selectedAgeTypes.join(",")}_${selectedSubsidyTypes.join(
      ","
    )}_${selectedExtraDayTypes.join(",")}`;
  };

  // Get the current value (edited or original)
  // Returns number for single value or 'mix' for mixed values
  const getValue = (serviceCode: string, dayCode: number): number | "mix" => {
    const key = `${serviceCode}-${dayCode}`;

    // Check edited rates first - only if it was edited with current filter state
    const currentFilterKey = getCurrentFilterKey();
    const editedRate = editedRates.get(key);
    if (editedRate && editedRate.filters === currentFilterKey) {
      return editedRate.value;
    }

    // Get from rateMap which now properly returns 'mix' when values differ
    const value = rateMap.get(key);
    return value !== undefined ? value : 0;
  };

  // Handle pending adjustments from bulk actions
  useEffect(() => {
    if (!pendingAdjustment) return;

    const { type, operation, value, services, days } = pendingAdjustment;
    const newEditedRates = new Map(editedRates);
    const currentFilterKey = getCurrentFilterKey();

    // Determine which services to apply adjustment to
    const servicesToAdjust = services || SERVICE_CODES.slice();
    const daysToAdjust = days || DAYS.map((d) => d.code);

    // Apply adjustment to currently viewed rates only
    servicesToAdjust.forEach((serviceCode) => {
      daysToAdjust.forEach((dayCode) => {
        const key = `${serviceCode}-${dayCode}`;
        // Get current value - either edited or from the rate map
        const currentValue = getValue(serviceCode, dayCode);

        // Skip mix values for adjustments
        if (currentValue === "mix") {
          return;
        }

        let newValue: number;
        if (type === "percentage") {
          const multiplier =
            operation === "increase" ? 1 + value / 100 : 1 - value / 100;
          newValue = Math.round(currentValue * multiplier);
        } else {
          // Fixed amount adjustment (value is in display currency, need to convert to minor units)
          const adjustmentInRappen = chfToRappen(value);
          newValue =
            operation === "increase"
              ? currentValue + adjustmentInRappen
              : Math.max(0, currentValue - adjustmentInRappen);
        }

        // Store with current filter state
        newEditedRates.set(key, { value: newValue, filters: currentFilterKey });
      });
    });

    setEditedRates(newEditedRates);
    setHasChanges(true);

    // Notify parent that adjustment has been applied
    if (onAdjustmentApplied) {
      onAdjustmentApplied();
    }
  }, [pendingAdjustment]); // eslint-disable-line react-hooks/exhaustive-deps

  // Handle pending copy from bulk actions
  useEffect(() => {
    if (!pendingCopy) return;

    const { sourceService, targetService } = pendingCopy;
    const newEditedRates = new Map(editedRates);
    const currentFilterKey = getCurrentFilterKey();

    // Copy all 7 days from source service to target service
    // for the currently selected age type and subsidy state
    DAYS.forEach((day) => {
      const targetKey = `${targetService}-${day.code}`;

      // Get the value from source service (either edited or original)
      const sourceValue = getValue(sourceService, day.code);

      // Skip mix values for copy operations - we can't copy an ambiguous value
      if (sourceValue === "mix") {
        return;
      }

      // Set it for the target service with current filter state
      newEditedRates.set(targetKey, {
        value: sourceValue,
        filters: currentFilterKey,
      });
    });

    setEditedRates(newEditedRates);
    setHasChanges(true);

    // Notify parent that copy has been applied
    if (onCopyApplied) {
      onCopyApplied();
    }
  }, [pendingCopy]); // eslint-disable-line react-hooks/exhaustive-deps

  // Handle value change
  const handleValueChange = (
    serviceCode: string,
    dayCode: number,
    value: string
  ) => {
    const key = `${serviceCode}-${dayCode}`;
    const currentFilterKey = getCurrentFilterKey();

    // Store the original value if this is the first edit
    if (!originalRates.has(key)) {
      const originalValue = rateMap.get(key);
      if (originalValue !== undefined) {
        setOriginalRates((prev) => {
          const newMap = new Map(prev);
          newMap.set(key, originalValue);
          return newMap;
        });
      }
    }

    setEditedRates((prev) => {
      const newMap = new Map(prev);
      if (value === "") {
        // If user clears the field, remove from edited rates
        newMap.delete(key);
      } else {
        const numValue = parseFloat(value) || 0;
        const rappenValue = chfToRappen(numValue);

        // Check if this value matches the original
        const originalValue = originalRates.get(key) ?? rateMap.get(key);
        if (originalValue !== "mix" && rappenValue === originalValue) {
          // Value is same as original, remove the edit
          newMap.delete(key);
        } else {
          // Store the value along with the current filter state
          newMap.set(key, { value: rappenValue, filters: currentFilterKey });
        }
      }
      return newMap;
    });

    // Recalculate hasChanges
    setHasChanges(
      editedRates.size > 0 ||
        (value !== "" &&
          chfToRappen(parseFloat(value) || 0) !==
            (originalRates.get(key) ?? rateMap.get(key)))
    );
  };

  // Reset changes
  const handleReset = () => {
    setEditedRates(new Map());
    setOriginalRates(new Map());
    setHasChanges(false);
  };

  // Pre-save verification
  const performPreSaveVerification = () => {
    if (!hasChanges) return null;

    let updatedRates: TariffRate[] = [];
    const affectedServices = new Set<string>();
    const affectedCombinations = new Set<string>();
    const sampleChanges: Array<{
      service: string;
      day: string;
      oldRate: number;
      newRate: number;
    }> = [];

    if (viewMode === "smart" && smartPatternApplyFn) {
      // Ask smart view to compute the latest payload synchronously
      const patternRates = smartPatternApplyFn();
      if (patternRates && patternRates.length > 0) {
        updatedRates = patternRates;

        // Analyze pattern rates
        patternRates.forEach((rate) => {
          // Smart-pattern rates are always generated with a service_code; the
          // generated TariffRate model marks every field optional.
          affectedServices.add(rate.service_code!);
          const combo = `${rate.age_type}_${rate.subsidised ? "sub" : "std"}_${
            rate.is_extra_day ? "extra" : "booked"
          }`;
          affectedCombinations.add(combo);
        });

        // Get sample changes (first 5)
        patternRates.slice(0, 5).forEach((rate) => {
          const key = `${rate.service_code}-${rate.day_of_week}`;
          const originalValue = rateMap.get(key);
          if (originalValue !== undefined && originalValue !== "mix") {
            sampleChanges.push({
              // See above: pattern rates always carry a service_code.
              service: rate.service_code!,
              day: getDayShortLabel(
                DAYS.find((d) => d.code === rate.day_of_week)?.name || ""
              ),
              oldRate: originalValue as number,
              newRate: rate.daily_rate || 0,
            });
          }
        });
      }
    } else {
      // Generate rates for grid mode
      editedRates.forEach((editedData, key) => {
        const [serviceCode, dayCode] = key.split("-");
        const filterParts = editedData.filters.split("_");
        const editedAgeTypes = filterParts[0].split(",") as AgeType[];
        const editedSubsidyTypes = filterParts[1]
          .split(",")
          .map((s) => s as SubsidyType);
        const editedExtraDayTypes = filterParts[2]
          .split(",")
          .map((s) => s as ExtraDayType);

        affectedServices.add(serviceCode);

        // Track combinations
        editedAgeTypes.forEach((ageType) => {
          editedSubsidyTypes.forEach((subsidyType) => {
            editedExtraDayTypes.forEach((extraDayType) => {
              const combo = `${ageType}_${
                subsidyType === "SUBSIDISED" ? "sub" : "std"
              }_${extraDayType === "EXTRA" ? "extra" : "booked"}`;
              affectedCombinations.add(combo);

              updatedRates.push({
                service_code: serviceCode,
                day_of_week: parseInt(dayCode),
                age_type: ageType,
                subsidised: subsidyType === "SUBSIDISED",
                is_extra_day: extraDayType === "EXTRA",
                daily_rate: editedData.value,
              } as TariffRate);
            });
          });
        });

        // Add sample changes
        if (sampleChanges.length < 5) {
          const originalValue = rateMap.get(key);
          if (originalValue !== undefined && originalValue !== "mix") {
            sampleChanges.push({
              service: serviceCode,
              day: getDayShortLabel(
                DAYS.find((d) => d.code === parseInt(dayCode))?.name || ""
              ),
              oldRate: originalValue as number,
              newRate: editedData.value,
            });
          }
        }
      });
    }

    return {
      totalRecords: updatedRates.length,
      services: Array.from(affectedServices),
      combinations: Array.from(affectedCombinations),
      sampleChanges,
      rates: updatedRates,
    };
  };

  // Save changes with verification
  const handleSave = async () => {
    if (!onSave || !hasChanges) return;

    // Perform pre-save verification
    const verification = performPreSaveVerification();
    if (!verification) {
      toast.error(i18n._(t`No changes to save`));
      return;
    }

    // Show confirmation dialog
    setPreSaveVerification({
      totalRecords: verification.totalRecords,
      services: verification.services,
      combinations: verification.combinations,
      sampleChanges: verification.sampleChanges,
    });
    setShowPreSaveDialog(true);
  };

  // Confirmed save after pre-verification
  const handleConfirmedSave = async () => {
    setShowPreSaveDialog(false);
    if (!onSave || !hasChanges) return;

    // Build the rates to save
    let updatedRates: TariffRate[] = [];

    if (viewMode === "smart" && smartPatternApplyFn) {
      // Ask smart view to compute the latest payload synchronously
      const patternRates = smartPatternApplyFn();
      // Use the computed pattern payload directly if available
      if (patternRates && patternRates.length > 0) {
        updatedRates = patternRates;
      }
    } else {
      // Generate rates ONLY for the filter combinations that were actually edited
      editedRates.forEach((editedData, key) => {
        const [serviceCode, dayCode] = key.split("-");
        const filterParts = editedData.filters.split("_");
        const editedAgeTypes = filterParts[0].split(",") as AgeType[];
        const editedSubsidyTypes = filterParts[1]
          .split(",")
          .map((s) => s as SubsidyType);
        const editedExtraDayTypes = filterParts[2]
          .split(",")
          .map((s) => s as ExtraDayType);

        // Create rates only for the combinations that were actually edited
        editedAgeTypes.forEach((ageType) => {
          editedSubsidyTypes.forEach((subsidyType) => {
            editedExtraDayTypes.forEach((extraDayType) => {
              updatedRates.push({
                service_code: serviceCode,
                day_of_week: parseInt(dayCode),
                age_type: ageType,
                subsidised: subsidyType === "SUBSIDISED",
                is_extra_day: extraDayType === "EXTRA",
                daily_rate: editedData.value,
              } as TariffRate);
            });
          });
        });
      });
    }

    // Guard: no updates
    if (!updatedRates || updatedRates.length === 0) {
      toast.error(i18n._(t`No changes to save`));
      return;
    }

    // Validate and deduplicate payload using full composite key
    const seen = new Set<string>();
    const sanitized: TariffRate[] = [];
    for (const r of updatedRates) {
      if (!r || !r.service_code) {
        console.warn(
          "[TariffUnifiedGrid] Skipping invalid rate: missing service_code",
          r
        );
        continue;
      }
      const day = Number(r.day_of_week);
      if (!Number.isInteger(day) || day < 1 || day > 7) {
        console.warn(
          "[TariffUnifiedGrid] Skipping invalid rate: invalid day",
          r
        );
        continue;
      }
      const rate = Math.max(0, Math.round(Number(r.daily_rate || 0)));
      // Use consistent composite key format everywhere
      const compositeKey = `${r.service_code}|${day}|${r.age_type}|${
        r.subsidised ? 1 : 0
      }|${r.is_extra_day ? 1 : 0}`;
      if (seen.has(compositeKey)) {
        console.warn(
          "[TariffUnifiedGrid] Skipping duplicate rate with key:",
          compositeKey
        );
        continue;
      }
      seen.add(compositeKey);
      sanitized.push({
        service_code: r.service_code,
        day_of_week: day,
        age_type: r.age_type,
        subsidised: !!r.subsidised,
        is_extra_day: !!r.is_extra_day,
        daily_rate: rate,
      } as TariffRate);
    }

    if (sanitized.length === 0) {
      toast.error(i18n._(t`No valid changes to save`));
      return;
    }

    // Show confirmation with exact details
    // Confirmation summary removed to reduce noise

    // Debug: summarize payload
    // Debug submission summary removed

    // Set saving state
    setIsSaving(true);
    setSaveProgress({ current: 0, total: 0, service: "" });

    // Initialize verification tracking
    const verificationTracking = {
      attempted: sanitized.length,
      succeeded: 0,
      failed: [] as Array<{ service: string; day: number; error: string }>,
      discrepancies: [] as Array<{
        service: string;
        day: number;
        expected: number;
        actual: number;
      }>,
    };

    try {
      // Save all the rates at once with progress tracking
      await onSave(sanitized, {
        onProgress: (current, total, service) => {
          setSaveProgress({ current, total, service: service || "" });
          // Track successful saves
          if (current > verificationTracking.succeeded) {
            verificationTracking.succeeded = current;
          }
        },
        versionTag: versionUpdatedAt, // Pass version tag for optimistic locking
      });

      // Mark all as succeeded if we get here
      verificationTracking.succeeded = sanitized.length;

      // After save, data is refetched via query invalidation in hooks; verification UI shows if issues occur

      // Show verification report if there were any issues
      if (
        verificationTracking.failed.length > 0 ||
        verificationTracking.discrepancies.length > 0
      ) {
        setVerificationReport(verificationTracking);
        setShowVerificationReport(true);
      } else {
        // Show success with details
        toast.success(
          i18n._(
            t`Successfully updated ${verificationTracking.succeeded} tariff rates`
          )
        );
      }

      // Don't clear editedRates here - let the useEffect handle it when new data arrives
      // This keeps the edited values visible until the server responds
    } catch (error) {
      // Track the failure
      const errorMessage = errorToastText(
        error,
        i18n,
        msg({ id: "tariffs.save.failed", message: "The tariff rates could not be saved." }),
      );
      verificationTracking.failed.push({
        service: "all",
        day: 0,
        error: errorMessage,
      });

      setVerificationReport(verificationTracking);
      setShowVerificationReport(true);

      // Re-throw to maintain existing error handling
      throw error;
    } finally {
      setIsSaving(false);
      setSaveProgress({ current: 0, total: 0, service: "" });
    }
  };

  // getServiceTimes/getServiceDisplay moved above for reuse in measurement

  const getDayShortLabel = (dayName: string) => {
    switch (dayName) {
      case "monday":
        return i18n._({ id: "common.days.short.monday", message: "Mon" });
      case "tuesday":
        return i18n._({ id: "common.days.short.tuesday", message: "Tue" });
      case "wednesday":
        return i18n._({ id: "common.days.short.wednesday", message: "Wed" });
      case "thursday":
        return i18n._({ id: "common.days.short.thursday", message: "Thu" });
      case "friday":
        return i18n._({ id: "common.days.short.friday", message: "Fri" });
      case "saturday":
        return i18n._({ id: "common.days.short.saturday", message: "Sat" });
      case "sunday":
        return i18n._({ id: "common.days.short.sunday", message: "Sun" });
      default:
        return dayName;
    }
  };

  const handlePatternApply = async (
    pattern: PatternType,
    newRates: TariffRate[]
  ) => {
    // Update local state with the new rates from the pattern
    const newEditedRates = new Map<
      string,
      { value: number; filters: string }
    >();
    const currentFilterKey = getCurrentFilterKey();

    newRates.forEach((rate) => {
      // For smart view pattern rates, we don't need to filter - the smart view already did that
      // Just map all rates to our editedRates format
      const key = `${rate.service_code}-${rate.day_of_week}`;
      // Store the rate in editedRates with current filter state
      newEditedRates.set(key, {
        value: rate.daily_rate || 0,
        filters: currentFilterKey,
      });
    });

    // Update edited rates
    setEditedRates(newEditedRates);
    setHasChanges(true);

    // Do NOT save immediately - wait for explicit Save button click
    // The changes are tracked locally and will be saved via handleSave

    // Call the parent handler if provided
    if (onApplyPattern) {
      onApplyPattern(pattern, newRates);
    }
  };

  // No toggle functions needed - direct setState calls in the UI

  return (
    <Card>
      <CardHeader>
        <div className="space-y-4">
          {/* Title and Badge Row */}
          <div className="flex items-start justify-between">
            <div>
              <CardTitle>{versionName}</CardTitle>
              {versionDescription && (
                <CardDescription className="mt-1">
                  {versionDescription}
                </CardDescription>
              )}
            </div>
            {/* All badges aligned to the right */}
            <div className="flex items-center gap-2">
              {isDefault && (
                <Badge variant="info">
                  <Trans id="tariffs.badge.default">DEFAULT</Trans>
                </Badge>
              )}
              {status && <TariffStatusBadge status={status} />}
              <Badge className="bg-[hsl(var(--chart-1))] text-white cursor-default hover:bg-[hsl(var(--chart-1))]">
                {groupType}
              </Badge>
            </div>
          </div>

          {/* Controls Row */}
          <div className="flex items-center justify-between flex-wrap gap-3">
            {/* Left side controls */}
            <div className="flex items-center gap-3">
              {/* Age Type Toggle */}
              <div className="flex gap-1 p-1 bg-muted rounded-md">
                <Button
                  size="sm"
                  variant={selectedAgeType === "BABY" ? "default" : "ghost"}
                  onClick={() => setSelectedAgeType("BABY")}
                  className="h-7"
                  aria-label={i18n._(t`Show tariffs for babies`)}
                  aria-pressed={selectedAgeType === "BABY"}
                >
                  {i18n._(t`Baby`)}
                </Button>
                <Button
                  size="sm"
                  variant={selectedAgeType === "BOTH" ? "default" : "ghost"}
                  onClick={() => setSelectedAgeType("BOTH")}
                  className="h-7"
                  aria-label={i18n._(
                    t`Show tariffs for both babies and children`
                  )}
                  aria-pressed={selectedAgeType === "BOTH"}
                >
                  <ChevronsLeftRight className="w-4 h-4" />
                </Button>
                <Button
                  size="sm"
                  variant={selectedAgeType === "CHILD" ? "default" : "ghost"}
                  onClick={() => setSelectedAgeType("CHILD")}
                  className="h-7"
                  aria-label={i18n._(t`Show tariffs for children`)}
                  aria-pressed={selectedAgeType === "CHILD"}
                >
                  {i18n._(t`Child`)}
                </Button>
              </div>

              {/* Subsidy Type Toggle */}
              <div className="flex gap-1 p-1 bg-muted rounded-md">
                <Button
                  size="sm"
                  variant={
                    selectedSubsidyType === "STANDARD" ? "default" : "ghost"
                  }
                  onClick={() => setSelectedSubsidyType("STANDARD")}
                  className="h-7"
                  aria-label={i18n._(t`Show standard tariffs`)}
                  aria-pressed={selectedSubsidyType === "STANDARD"}
                >
                  {i18n._(t`Standard`)}
                </Button>
                <Button
                  size="sm"
                  variant={selectedSubsidyType === "BOTH" ? "default" : "ghost"}
                  onClick={() => setSelectedSubsidyType("BOTH")}
                  className="h-7"
                  aria-label={i18n._(
                    t`Show both standard and subsidised tariffs`
                  )}
                  aria-pressed={selectedSubsidyType === "BOTH"}
                >
                  <ChevronsLeftRight className="w-4 h-4" />
                </Button>
                <Button
                  size="sm"
                  variant={
                    selectedSubsidyType === "SUBSIDISED" ? "default" : "ghost"
                  }
                  onClick={() => setSelectedSubsidyType("SUBSIDISED")}
                  className="h-7"
                  aria-label={i18n._(t`Show subsidised tariffs`)}
                  aria-pressed={selectedSubsidyType === "SUBSIDISED"}
                >
                  {i18n._(t`Subsidised`)}
                </Button>
              </div>

              {/* Extra Day Type Toggle */}
              <div className="flex gap-1 p-1 bg-muted rounded-md">
                <Button
                  size="sm"
                  variant={
                    selectedExtraDayType === "BOOKED" ? "default" : "ghost"
                  }
                  onClick={() => setSelectedExtraDayType("BOOKED")}
                  className="h-7"
                  aria-label={i18n._(t`Show booked day tariffs`)}
                  aria-pressed={selectedExtraDayType === "BOOKED"}
                >
                  {i18n._(t`Booked`)}
                </Button>
                <Button
                  size="sm"
                  variant={
                    selectedExtraDayType === "BOTH" ? "default" : "ghost"
                  }
                  onClick={() => setSelectedExtraDayType("BOTH")}
                  className="h-7"
                  aria-label={i18n._(t`Show both booked and extra day tariffs`)}
                  aria-pressed={selectedExtraDayType === "BOTH"}
                >
                  <ChevronsLeftRight className="w-4 h-4" />
                </Button>
                <Button
                  size="sm"
                  variant={
                    selectedExtraDayType === "EXTRA" ? "default" : "ghost"
                  }
                  onClick={() => setSelectedExtraDayType("EXTRA")}
                  className="h-7"
                  aria-label={i18n._(t`Show extra day tariffs`)}
                  aria-pressed={selectedExtraDayType === "EXTRA"}
                >
                  {i18n._(t`Extra`)}
                </Button>
              </div>
            </div>

            {/* Right side controls */}
            <div className="flex items-center gap-3">
              {/* Pattern Selector for Smart Mode */}
              {viewMode === "smart" && (
                <div className="flex gap-1 p-1 bg-muted rounded-md">
                  <Button
                    size="sm"
                    variant={
                      selectedPattern === "UNIFORM" ? "default" : "ghost"
                    }
                    onClick={() => setSelectedPattern("UNIFORM")}
                    className="h-7"
                    disabled={isLoading}
                    aria-label={i18n._(t`Select uniform pricing pattern`)}
                    aria-pressed={selectedPattern === "UNIFORM"}
                  >
                    {i18n._(t`Uniform`)}
                  </Button>
                  <Button
                    size="sm"
                    variant={
                      selectedPattern === "WEEKDAY_WEEKEND"
                        ? "default"
                        : "ghost"
                    }
                    onClick={() => setSelectedPattern("WEEKDAY_WEEKEND")}
                    className="h-7"
                    disabled={isLoading}
                    aria-label={i18n._(
                      t`Select weekday/weekend pricing pattern`
                    )}
                    aria-pressed={selectedPattern === "WEEKDAY_WEEKEND"}
                  >
                    {i18n._(t`Weekday/Weekend`)}
                  </Button>
                </div>
              )}

              {/* View Mode Toggle */}
              <div className="flex gap-1 p-1 bg-muted rounded-md">
                <Button
                  size="sm"
                  variant={viewMode === "smart" ? "default" : "ghost"}
                  onClick={() => setViewMode("smart")}
                  className="h-7"
                  aria-label={i18n._(t`Smart pricing mode`)}
                  aria-pressed={viewMode === "smart"}
                >
                  <Sparkles className="w-4 h-4 mr-1" />
                  {i18n._(t`Smart`)}
                </Button>
                <Button
                  size="sm"
                  variant={viewMode === "grid" ? "default" : "ghost"}
                  onClick={() => setViewMode("grid")}
                  className="h-7"
                  aria-label={i18n._(t`Grid pricing mode`)}
                  aria-pressed={viewMode === "grid"}
                >
                  <Grid3x3 className="w-4 h-4 mr-1" />
                  {i18n._(t`Grid`)}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </CardHeader>

      <CardContent>
        {viewMode === "grid" ? (
          <Table className="table-fixed">
            <colgroup>
              <col style={{ width: `${SERVICE_COL_PCT}%` }} />
              {DAYS.map((d) => (
                <col key={d.code} style={{ width: `${DAY_COL_PCT}%` }} />
              ))}
            </colgroup>
            <TableHeader>
              <TableRow>
                <TableHead>{i18n._(t`Service`)}</TableHead>
                {DAYS.map((day) => (
                  <TableHead key={day.code} className="text-center">
                    {getDayShortLabel(day.name)}
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {SERVICE_CODES.map((serviceCode) => {
                const { name, times } = getServiceDisplay(serviceCode);
                return (
                  <TableRow key={serviceCode}>
                    <TableCell className="font-medium">
                      <div className="flex items-center justify-between w-full min-w-0">
                        <span className="min-w-0 truncate">{name}</span>
                        {times && (
                          <span className="text-xs font-normal text-muted-foreground whitespace-nowrap ml-2 shrink-0">
                            {times}
                          </span>
                        )}
                      </div>
                    </TableCell>
                    {DAYS.map((day) => {
                      const key = `${serviceCode}-${day.code}`;
                      const value = getValue(serviceCode, day.code);
                      const originalValue =
                        originalRates.get(key) ?? rateMap.get(key);
                      const currentFilterKey = getCurrentFilterKey();
                      const editedRate = editedRates.get(key);
                      const isEdited =
                        editedRate &&
                        editedRate.filters === currentFilterKey &&
                        (originalValue === "mix" ||
                          editedRate.value !== originalValue);
                      const isMix = value === "mix";

                      return (
                        <TableCell key={day.code} className="p-1">
                          {readOnly ? (
                            <div className="text-center">
                              {isMix
                                ? "mix"
                                : formatRappen(value as number, currency)}
                            </div>
                          ) : (
                            <Input
                              type="number"
                              step="0.01"
                              min={0}
                              value={isMix ? "" : rappenToCHF(value as number)}
                              placeholder={isMix ? "mix" : undefined}
                              onChange={(e) =>
                                handleValueChange(
                                  serviceCode,
                                  day.code,
                                  e.target.value
                                )
                              }
                              className={`text-center text-sm ${
                                isEdited ? "tariff-field-edited" : ""
                              } ${
                                isMix && !isEdited
                                  ? "text-muted-foreground"
                                  : ""
                              }`}
                              disabled={isLoading}
                            />
                          )}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        ) : (
          <TariffSmartPatternView
            selectedPattern={selectedPattern}
            rates={rates}
            editedRates={editedRates}
            onApplyPattern={handlePatternApply}
            isLoading={isLoading || readOnly}
            selectedAgeTypes={selectedAgeTypes}
            selectedSubsidyTypes={selectedSubsidyTypes}
            selectedExtraDayTypes={selectedExtraDayTypes}
            services={services}
            onPendingChanges={setHasChanges}
            onApplyRequest={registerSmartPatternApply}
          />
        )}

        {/* Save/Reset Buttons - Show when there are changes */}
        {!readOnly && hasChanges && (
          <div className="mt-4 space-y-2">
            {/* Progress indicator when saving */}
            {isSaving && saveProgress.total > 0 && (
              <div className="space-y-2">
                <div className="flex items-center justify-between text-sm text-muted-foreground">
                  <span>
                    {saveProgress.service ? (
                      <Trans id="tariffs.savingService">
                        Saving {saveProgress.service} ({saveProgress.current}/
                        {saveProgress.total})
                      </Trans>
                    ) : (
                      <Trans id="tariffs.preparingSave">
                        Preparing to save...
                      </Trans>
                    )}
                  </span>
                  <span>
                    {Math.round(
                      (saveProgress.current / saveProgress.total) * 100
                    )}
                    %
                  </span>
                </div>
                <Progress
                  value={(saveProgress.current / saveProgress.total) * 100}
                  className="h-2"
                />
              </div>
            )}

            <div className="flex justify-end">
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleReset}
                  disabled={isLoading || isSaving}
                  aria-label={i18n._(msg({ id: "tariffs.grid.undoChanges", message: "Undo changes" }))}
                >
                  <Undo className="w-4 h-4" aria-hidden="true" />
                </Button>
                <Button
                  size="sm"
                  onClick={handleSave}
                  disabled={isLoading || isSaving}
                >
                  {isSaving ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                      {i18n._(t({ id: "common.saving", message: "Saving..." }))}
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4 mr-1" />
                      {i18n._(t({ id: "common.save", message: "Save" }))}
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>
        )}
      </CardContent>

      {/* Pre-Save Verification Dialog */}
      <Dialog open={showPreSaveDialog} onOpenChange={setShowPreSaveDialog}>
        <DialogContent size="lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-warning-on-surface" />
              <Trans>Confirm Tariff Updates</Trans>
            </DialogTitle>
            <DialogDescription>
              <Trans>Please review the changes before saving</Trans>
            </DialogDescription>
          </DialogHeader>

          {preSaveVerification && (
            <div className="space-y-4">
              {/* Summary */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <p className="text-sm font-medium text-muted-foreground">
                    <Trans>Total Records to Update</Trans>
                  </p>
                  <p className="text-2xl font-bold">
                    {preSaveVerification.totalRecords}
                  </p>
                </div>
                <div className="space-y-2">
                  <p className="text-sm font-medium text-muted-foreground">
                    <Trans>Affected Services</Trans>
                  </p>
                  <p className="text-2xl font-bold">
                    {preSaveVerification.services.length}
                  </p>
                </div>
              </div>

              {/* Services List */}
              <div className="space-y-2">
                <p className="text-sm font-medium">
                  <Trans>Services Being Updated:</Trans>
                </p>
                <div className="flex flex-wrap gap-2">
                  {preSaveVerification.services.map((service) => (
                    <Badge key={service} variant="secondary">
                      {getServiceDisplay(service).name}
                    </Badge>
                  ))}
                </div>
              </div>

              {/* Filter Combinations */}
              <div className="space-y-2">
                <p className="text-sm font-medium">
                  <Trans>Filter Combinations:</Trans>
                </p>
                <div className="flex flex-wrap gap-2">
                  {preSaveVerification.combinations.map((combo) => {
                    const parts = combo.split("_");
                    const ageType = parts[0];
                    const subsidyType =
                      parts[1] === "sub" ? "Subsidised" : "Standard";
                    const extraType = parts[2] === "extra" ? "Extra" : "Booked";
                    return (
                      <Badge key={combo} variant="outline">
                        {`${ageType} • ${subsidyType} • ${extraType}`}
                      </Badge>
                    );
                  })}
                </div>
              </div>

              {/* Sample Changes */}
              {preSaveVerification.sampleChanges.length > 0 && (
                <div className="space-y-2">
                  <p className="text-sm font-medium">
                    <Trans>Sample Changes (first 5):</Trans>
                  </p>
                  <ScrollArea className="h-32 w-full rounded-md border p-4">
                    <div className="space-y-2">
                      {preSaveVerification.sampleChanges.map((change, idx) => (
                        <div key={idx} className="flex justify-between text-sm">
                          <span className="text-muted-foreground">
                            {getServiceDisplay(change.service).name} -{" "}
                            {change.day}
                          </span>
                          <span>
                            {formatRappen(change.oldRate, currency)} →{" "}
                            {formatRappen(change.newRate, currency)}
                            {change.newRate > change.oldRate && (
                              <span className="text-success-on-surface ml-2">
                                +
                                {formatRappen(
                                  change.newRate - change.oldRate,
                                  currency
                                )}
                              </span>
                            )}
                            {change.newRate < change.oldRate && (
                              <span className="text-destructive ml-2">
                                -
                                {formatRappen(
                                  change.oldRate - change.newRate,
                                  currency
                                )}
                              </span>
                            )}
                          </span>
                        </div>
                      ))}
                    </div>
                  </ScrollArea>
                </div>
              )}

              <Alert>
                <FileWarning className="h-4 w-4" />
                <AlertTitle>
                  <Trans>Important</Trans>
                </AlertTitle>
                <AlertDescription>
                  <Trans>
                    This will update {preSaveVerification.totalRecords} tariff
                    rates. The changes cannot be automatically reversed.
                  </Trans>
                </AlertDescription>
              </Alert>
            </div>
          )}

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowPreSaveDialog(false)}
              disabled={isSaving}
            >
              <Trans>Cancel</Trans>
            </Button>
            <Button onClick={handleConfirmedSave} disabled={isSaving}>
              {isSaving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  <Trans>Saving...</Trans>
                </>
              ) : (
                <>
                  <CheckCircle className="w-4 h-4 mr-2" />
                  <Trans>Confirm & Save</Trans>
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Post-Save Verification Report */}
      <Dialog
        open={showVerificationReport}
        onOpenChange={setShowVerificationReport}
      >
        <DialogContent size="lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {verificationReport && verificationReport.failed.length === 0 ? (
                <>
                  <CheckCircle className="h-5 w-5 text-success-on-surface" />
                  <Trans>Update Verification Report</Trans>
                </>
              ) : (
                <>
                  <XCircle className="h-5 w-5 text-destructive" />
                  <Trans>Update Issues Detected</Trans>
                </>
              )}
            </DialogTitle>
            <DialogDescription>
              <Trans id="tariffs.verification.description">
                This report shows how many tariff rates were saved and which ones failed.
              </Trans>
            </DialogDescription>
          </DialogHeader>

          {verificationReport && (
            <div className="space-y-4">
              {/* Summary Stats */}
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <p className="text-sm font-medium text-muted-foreground">
                    <Trans>Attempted</Trans>
                  </p>
                  <p className="text-2xl font-bold">
                    {verificationReport.attempted}
                  </p>
                </div>
                <div className="space-y-2">
                  <p className="text-sm font-medium text-success-on-surface">
                    <Trans>Succeeded</Trans>
                  </p>
                  <p className="text-2xl font-bold text-success-on-surface">
                    {verificationReport.succeeded}
                  </p>
                </div>
                <div className="space-y-2">
                  <p className="text-sm font-medium text-destructive">
                    <Trans>Failed</Trans>
                  </p>
                  <p className="text-2xl font-bold text-destructive">
                    {verificationReport.failed.length}
                  </p>
                </div>
              </div>

              {/* Success Rate Bar */}
              <div className="space-y-2">
                <p className="text-sm font-medium">
                  <Trans>Success Rate</Trans>
                </p>
                <Progress
                  value={
                    (verificationReport.succeeded /
                      verificationReport.attempted) *
                    100
                  }
                  className="h-2"
                />
                <p className="text-xs text-muted-foreground text-right">
                  {Math.round(
                    (verificationReport.succeeded /
                      verificationReport.attempted) *
                      100
                  )}
                  %
                </p>
              </div>

              {/* Failed Updates */}
              {verificationReport.failed.length > 0 && (
                <Alert variant="destructive">
                  <XCircle className="h-4 w-4" />
                  <AlertTitle>
                    <Trans>Failed Updates</Trans>
                  </AlertTitle>
                  <AlertDescription>
                    <ScrollArea className="h-32 w-full mt-2">
                      {verificationReport.failed.map((failure, idx) => (
                        <div key={idx} className="text-sm mb-2">
                          <span className="font-medium">
                            {failure.service === "all"
                              ? "All services"
                              : getServiceDisplay(failure.service).name}
                          </span>
                          {failure.day > 0 && (
                            <span className="ml-2 text-muted-foreground">
                              Day {failure.day}
                            </span>
                          )}
                          <p className="text-xs text-muted-foreground mt-1">
                            {failure.error}
                          </p>
                        </div>
                      ))}
                    </ScrollArea>
                  </AlertDescription>
                </Alert>
              )}

              {/* Discrepancies */}
              {verificationReport.discrepancies.length > 0 && (
                <Alert>
                  <AlertTriangle className="h-4 w-4" />
                  <AlertTitle>
                    <Trans>Data Discrepancies</Trans>
                  </AlertTitle>
                  <AlertDescription>
                    <Trans>
                      The following rates differ from what was requested:
                    </Trans>
                    <ScrollArea className="h-32 w-full mt-2">
                      {verificationReport.discrepancies.map((disc, idx) => (
                        <div key={idx} className="text-sm mb-2">
                          {getServiceDisplay(disc.service).name} - Day{" "}
                          {disc.day}: Expected{" "}
                          {formatRappen(disc.expected, currency)}, got{" "}
                          {formatRappen(disc.actual, currency)}
                        </div>
                      ))}
                    </ScrollArea>
                  </AlertDescription>
                </Alert>
              )}

              {/* Retry Option for Failed Updates */}
              {verificationReport.failed.length > 0 && (
                <Alert>
                  <AlertTriangle className="h-4 w-4" />
                  <AlertTitle>
                    <Trans>Retry Failed Updates</Trans>
                  </AlertTitle>
                  <AlertDescription>
                    <Trans>
                      Some updates failed. You can try saving again or contact
                      support if the issue persists.
                    </Trans>
                  </AlertDescription>
                </Alert>
              )}
            </div>
          )}

          <DialogFooter>
            <Button onClick={() => setShowVerificationReport(false)}>
              <Trans>Close</Trans>
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
